import { Button } from '@/shared/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/shared/ui/card';
import { Copy } from 'lucide-react';
import {
  clientToContentPoint,
  LayoutAssetSceneViewport,
  type LayoutAssetSceneViewportProps,
  type PdfRasterFallback,
  normalizeLayoutPolygon,
  usePolygonEditing,
} from '@ironview/interactive-viewer';
import { AreaTagSetLassoPath } from '@/features/area-tag-sets';
import { Unit, ImageDimensions, PhotoMarker, Position } from '../types/types';
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useDesktopViewer } from '../hooks/useDesktopViewer';
import { trackEvent } from '@/shared/utils/google-analytics';
import {
  HeatMapIssueRange,
  HeatMapSummary,
  getHeatMapColor,
  getHeatMapSoftColor,
  issueCountMatchesRange,
} from '../utils/heatMap';
import { SelectedAreaFocusControl } from './SelectedAreaFocusControl';
import {
  normalizePdfRotation,
  toViewerAreaTag,
  toViewerLayoutMarker,
} from '../adapters/ironview360Adapter';
import {
  createIronview360AreaTagLayerProps,
  createIronview360MarkerLayerProps,
} from './ironview360SceneAdapter';

const MARKER_TAP_MOVE_TOLERANCE_PX = 10;

interface LayoutCanvasProps {
  getCursor: () => string;
  imageDimensions: ImageDimensions;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleSvgClick: (e: React.MouseEvent) => void;
  selectedLayoutId: string;
  units: Unit[];
  photoMarkers: PhotoMarker[];
  svgRef: React.RefObject<SVGSVGElement>;
  groupRef: React.RefObject<SVGGElement>;
  position: Position;
  scale: number;
  imagePath: string;
  pdfUrl?: string;
  pdfPageNumber?: number;
  pdfRotation?: number;
  pdfCropRegion?: [number, number, number, number] | null;
  pdfRasterFallback?: PdfRasterFallback;
  onDimensionsLoad?: (dimensions: ImageDimensions) => void;
  onFitComplete?: (fitScale: number, fitPosition: Position) => void;
  onScaleChange?: (scale: number) => void;
  onPositionChange?: (position: Position) => void;
  addPhotoMarker?: (point: Position) => void;
  setSelectedMarkers?: (markers: Set<number>) => void;
  flippedHorizontal?: boolean;
  flippedVertical?: boolean;
  selectedUnit: Unit | null;
  currentPolygon: number[][];
  selectedMarkers: Set<number>;
  markersInAreas: Set<PhotoMarker>;
  areaPhotoMap: Map<number, number[]>;
  areaMarkerMap: Map<number, number[]>;
  areaHeatMapSummaries: Map<number, HeatMapSummary>;
  markerHeatMapSummaries: Map<number, HeatMapSummary>;
  heatMapIssueFilter: HeatMapIssueRange;
  isHeatMapEnabled: boolean;
  photoIndexMode: boolean;
  showCoordinates: boolean;
  mode?: 'view' | 'measure' | 'photoIndex' | 'areaTags';
  handleUnitClick: (unit: Unit, event: React.MouseEvent) => void;
  handleMarkerClick: (marker: PhotoMarker, event: React.MouseEvent) => void;
  deletePhotoMarker: (markerId: number) => void;
  handleMarkerRightClick?: (event: React.MouseEvent) => void;
  handleMarkerDragStart?: (markerId: number, event: React.MouseEvent) => void;
  onWheelZoom?: (e: React.WheelEvent) => void;
  handleTouchStart?: (e: React.TouchEvent) => void;
  handleTouchMove?: (e: React.TouchEvent) => void;
  handleTouchEnd?: (e: React.TouchEvent) => void;
  disableMarkerClicks?: boolean;
  textEditingMode?: boolean;
  onRequestLabelEdit?: (areaId: number) => void;
  onBackToOverview?: () => void;
  onReady?: () => void;
  pendingTextChanges?: Map<
    number,
    Partial<{
      name: string;
      text_position: [number, number];
      text_size: number;
      text_rotation: number;
    }>
  >;
  onSaveTextChanges?: () => void;
  onCancelTextChanges?: () => void;
  polygonEditingMode?: boolean;
  onPolygonUpdate?: (areaId: number, newCoordinates: number[][]) => void;
  pendingPolygonChanges?: Map<number, number[][]>;
  showAreaDeleteControls?: boolean;
  onDeleteAreaTag?: (areaId: number) => void;
  areaTagSetSelection?: ReadonlySet<number>;
}

const LayoutCanvas = ({
  getCursor,
  imageDimensions,
  handleMouseDown,
  handleSvgClick,
  selectedLayoutId,
  units,
  photoMarkers,
  svgRef,
  groupRef,
  position,
  scale,
  imagePath,
  pdfUrl,
  pdfPageNumber = 1,
  pdfRotation = 0,
  pdfCropRegion = null,
  pdfRasterFallback,
  onDimensionsLoad,
  onFitComplete,
  onScaleChange,
  onPositionChange,
  addPhotoMarker,
  setSelectedMarkers,
  flippedHorizontal = false,
  flippedVertical = false,
  selectedUnit,
  currentPolygon,
  selectedMarkers,
  markersInAreas,
  areaPhotoMap,
  areaMarkerMap,
  areaHeatMapSummaries,
  markerHeatMapSummaries,
  heatMapIssueFilter,
  isHeatMapEnabled,
  photoIndexMode,
  showCoordinates,
  mode = 'view',
  handleUnitClick,
  handleMarkerClick,
  deletePhotoMarker,
  handleMarkerRightClick,
  handleMarkerDragStart,
  onWheelZoom,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  disableMarkerClicks = false,
  textEditingMode = false,
  onRequestLabelEdit,
  onBackToOverview,
  onReady,
  pendingTextChanges = new Map(),
  onSaveTextChanges,
  onCancelTextChanges,
  polygonEditingMode = false,
  onPolygonUpdate,
  pendingPolygonChanges = new Map(),
  showAreaDeleteControls = false,
  onDeleteAreaTag,
  areaTagSetSelection,
}: LayoutCanvasProps) => {
  const isPdfLayout = Boolean(pdfUrl);
  const canShowTooltips = useDesktopViewer();
  const [hoveredUnitId, setHoveredUnitId] = useState<number | null>(null);
  const [heatMapTooltip, setHeatMapTooltip] = useState<{
    summary: HeatMapSummary;
    x: number;
    y: number;
  } | null>(null);
  useEffect(() => {
    // Drop stale hover state when changing input mode or reopening the heat map.
    setHeatMapTooltip(null);
    setHoveredUnitId(null);
  }, [canShowTooltips, isHeatMapEnabled]);
  const markerTouchStartRef = useRef<{ markerId: number; x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerAreaTags = useMemo(() => units.map(toViewerAreaTag), [units]);
  const sourceAreaTagsById = useMemo(
    () => new Map(units.map((unit) => [String(unit.id), unit] as const)),
    [units]
  );
  const sortedViewerAreaTags = useMemo(
    () =>
      [...viewerAreaTags].sort((a, b) => {
        const aIsHovered = String(a.id) === String(hoveredUnitId) ? 1 : 0;
        const bIsHovered = String(b.id) === String(hoveredUnitId) ? 1 : 0;
        return aIsHovered - bIsHovered;
      }),
    [hoveredUnitId, viewerAreaTags]
  );
  const viewerPhotoMarkers = useMemo(() => photoMarkers.map(toViewerLayoutMarker), [photoMarkers]);
  const sourcePhotoMarkersById = useMemo(
    () => new Map(photoMarkers.map((marker) => [String(marker.id), marker] as const)),
    [photoMarkers]
  );
  const allAreaPhotoIds = useMemo(() => {
    const ids = new Set<number>();
    areaPhotoMap.forEach((photoIds) => {
      photoIds.forEach((photoId) => {
        const normalized = Number(photoId);
        if (Number.isFinite(normalized)) ids.add(normalized);
      });
    });
    return ids;
  }, [areaPhotoMap]);

  const getSVGCoordinates = useCallback(
    (event: { clientX: number; clientY: number }): { x: number; y: number } => {
      if (!svgRef.current) return { x: 0, y: 0 };
      return (
        clientToContentPoint(svgRef.current, event.clientX, event.clientY, {
          isPdfLayout,
          position,
          scale,
        }) ?? {
          x: 0,
          y: 0,
        }
      );
    },
    [isPdfLayout, svgRef, position, scale]
  );

  const editablePolygons = useMemo(
    () => units.map((unit) => ({ id: unit.id, points: unit.points })),
    [units]
  );
  const {
    editingPolygonId,
    draggingNodeIndex,
    snapPosition,
    showSnapIndicator,
    handlePolygonEditClick,
    handleNodeDragStart,
  } = usePolygonEditing<number>({
    enabled: polygonEditingMode,
    polygons: editablePolygons,
    pendingPoints: pendingPolygonChanges,
    scale,
    getContentPoint: getSVGCoordinates,
    onPolygonUpdate: (areaId, points) =>
      onPolygonUpdate?.(
        areaId,
        points.map(([x, y]) => [x, y])
      ),
  });
  const editingPolygonPoints = useMemo(() => {
    if (!polygonEditingMode || editingPolygonId === null) return [];
    const unit = units.find((item) => item.id === editingPolygonId);
    if (!unit) return [];
    return normalizeLayoutPolygon(pendingPolygonChanges.get(editingPolygonId) ?? unit.points);
  }, [editingPolygonId, pendingPolygonChanges, polygonEditingMode, units]);

  const activatePhotoMarker = useCallback(
    (marker: PhotoMarker, event: React.MouseEvent<SVGGElement> | React.TouchEvent<SVGGElement>) => {
      event.stopPropagation();

      if (disableMarkerClicks) return;

      if (!photoIndexMode) {
        trackEvent('photo_marker', 'photo_marker', 'Property Documentation', {
          layout_id: selectedLayoutId,
          marker_number: marker.number,
        });
      }

      handleMarkerClick(marker, event as unknown as React.MouseEvent);
    },
    [disableMarkerClicks, handleMarkerClick, photoIndexMode, selectedLayoutId]
  );

  const showHeatMapTooltip = useCallback(
    (summary: HeatMapSummary | undefined, event: React.MouseEvent) => {
      if (!canShowTooltips || !isHeatMapEnabled || !summary) return;
      setHeatMapTooltip({ summary, x: event.clientX, y: event.clientY });
    },
    [canShowTooltips, isHeatMapEnabled]
  );

  const hideHeatMapTooltip = useCallback(() => {
    setHeatMapTooltip(null);
  }, []);

  const handlePhotoMarkerTouchStart = useCallback(
    (marker: PhotoMarker, event: React.TouchEvent<SVGGElement>) => {
      if (disableMarkerClicks || event.touches.length !== 1) return;

      const touch = event.touches[0];
      markerTouchStartRef.current = {
        markerId: marker.id,
        x: touch.clientX,
        y: touch.clientY,
      };

      // Android Chrome can suppress SVG clicks when the panning layer calls preventDefault.
      event.preventDefault();
      event.stopPropagation();
    },
    [disableMarkerClicks]
  );

  const handlePhotoMarkerTouchEnd = useCallback(
    (marker: PhotoMarker, event: React.TouchEvent<SVGGElement>) => {
      if (disableMarkerClicks) return;

      event.preventDefault();
      event.stopPropagation();

      const start = markerTouchStartRef.current;
      markerTouchStartRef.current = null;
      const touch = event.changedTouches[0];
      if (!start || !touch || start.markerId !== marker.id) return;

      const moved = Math.hypot(touch.clientX - start.x, touch.clientY - start.y);
      if (moved <= MARKER_TAP_MOVE_TOLERANCE_PX) {
        activatePhotoMarker(marker, event);
      }
    },
    [activatePhotoMarker, disableMarkerClicks]
  );

  const handlePdfBackgroundClick = useCallback(
    (x: number, y: number) => {
      const point = { x: Math.round(x), y: Math.round(y) };
      if (mode !== 'measure' && photoIndexMode) {
        addPhotoMarker?.(point);
      } else if (mode !== 'measure') {
        setSelectedMarkers?.(new Set());
      }
    },
    [addPhotoMarker, mode, photoIndexMode, setSelectedMarkers]
  );
  const handlePdfReady = useCallback(() => onReady?.(), [onReady]);

  const layoutAssetSceneViewportProps: LayoutAssetSceneViewportProps =
    isPdfLayout && pdfUrl
      ? {
          asset: {
            kind: 'pdf',
            url: pdfUrl,
            pageNumber: pdfPageNumber,
            rotation: normalizePdfRotation(pdfRotation),
            cropRegion: pdfCropRegion,
            rasterFallback: pdfRasterFallback,
            flippedHorizontal,
            flippedVertical,
          },
          position,
          scale,
          pdfViewport: {
            onScaleChange: (nextScale) => onScaleChange?.(nextScale),
            onPositionChange: (nextPosition) => onPositionChange?.(nextPosition),
            onFitComplete,
            onDimensionsLoad,
            onInitialRenderComplete: handlePdfReady,
            onInitialRenderError: handlePdfReady,
            onBackgroundClick: handlePdfBackgroundClick,
            loadingMessage: 'Loading Layout...',
          },
          pdfOverlay: {
            svgRef,
            groupRef,
          },
        }
      : {
          asset: {
            kind: 'image',
            url: imagePath,
            flippedHorizontal,
            flippedVertical,
          },
          position,
          scale,
          imageViewport: {
            svgRef,
            groupRef,
            dimensions: imageDimensions,
            cursor: getCursor(),
            onMouseDown: handleMouseDown,
            onClick: handleSvgClick,
            onWheel: onWheelZoom,
            onTouchStart: handleTouchStart,
            onTouchMove: handleTouchMove,
            onTouchEnd: handleTouchEnd,
            onImageReady: onReady,
          },
        };

  return (
    <div ref={containerRef} className="w-full h-full relative overflow-hidden">
      <LayoutAssetSceneViewport
        {...layoutAssetSceneViewportProps}
        scene={{
          areaTags: {
            ...createIronview360AreaTagLayerProps({
              areaTags: isPdfLayout ? viewerAreaTags : sortedViewerAreaTags,
              sourceAreaTagsById,
              pendingPolygonChanges,
              pendingTextChanges,
              areaHeatMapSummaries,
              heatMapIssueFilter,
              isHeatMapEnabled,
              showTooltips: canShowTooltips,
              selectedAreaId: selectedUnit?.id,
              hoveredAreaId: isPdfLayout ? undefined : (hoveredUnitId ?? undefined),
              fixedLabelFontSize: isPdfLayout ? undefined : 28,
              interactive: mode !== 'measure',
              highlightedAreaIds: areaTagSetSelection,
            }),
            onHoverStart: (areaTag, event) => {
              if (!canShowTooltips) return;
              const unit = sourceAreaTagsById.get(String(areaTag.id));
              if (!unit) return;
              const summary = areaHeatMapSummaries.get(unit.id);
              if (isPdfLayout) {
                if (isHeatMapEnabled) {
                  showHeatMapTooltip(summary, event);
                }
                return;
              }
              setHoveredUnitId(unit.id);
              if (isHeatMapEnabled) showHeatMapTooltip(summary, event);
            },
            onHoverMove: (areaTag, event) => {
              const unit = sourceAreaTagsById.get(String(areaTag.id));
              if (unit) {
                showHeatMapTooltip(areaHeatMapSummaries.get(unit.id), event);
              }
            },
            onHoverEnd: (areaTag) => {
              const unit = sourceAreaTagsById.get(String(areaTag.id));
              if (!unit) return;
              if (isPdfLayout) {
                if (isHeatMapEnabled) hideHeatMapTooltip();
                return;
              }
              setHoveredUnitId((previous) => (previous === unit.id ? null : previous));
              if (isHeatMapEnabled) hideHeatMapTooltip();
            },
            onActivate: (areaTag, event) => {
              const unit = sourceAreaTagsById.get(String(areaTag.id));
              if (!unit) return;
              if (polygonEditingMode) {
                handlePolygonEditClick(unit.id, event);
              } else if (textEditingMode) {
                event.stopPropagation();
                onRequestLabelEdit?.(unit.id);
              } else if (!isPdfLayout || mode === 'view') {
                if (isPdfLayout) event.stopPropagation();
                handleUnitClick(unit, event);
              }
            },
          },
          afterAreaTags:
            mode === 'measure' ? <AreaTagSetLassoPath points={currentPolygon} /> : undefined,
          markers: {
            ...createIronview360MarkerLayerProps({
              markers: viewerPhotoMarkers,
              sourcePhotoMarkersById,
              selectedMarkerIds: selectedMarkers,
              markerHeatMapSummaries,
              heatMapIssueFilter,
              isHeatMapEnabled,
              showTooltips: canShowTooltips,
              photoIndexMode,
              disableMarkerClicks,
              selectedAreaId: selectedUnit?.id,
              areaMarkerMap,
              areaPhotoMap,
              isIncludedInAnyArea: (marker, viewerMarker) =>
                markersInAreas.has(marker) ||
                (isPdfLayout &&
                  viewerMarker.photos.some((photo) => allAreaPhotoIds.has(Number(photo.id)))),
            }),
            onActivate: (viewerMarker, event) => {
              const marker = sourcePhotoMarkersById.get(String(viewerMarker.id));
              if (marker) activatePhotoMarker(marker, event);
            },
            onHoverStart: (viewerMarker, event) => {
              const marker = sourcePhotoMarkersById.get(String(viewerMarker.id));
              if (marker && isHeatMapEnabled) {
                showHeatMapTooltip(markerHeatMapSummaries.get(marker.id), event);
              }
            },
            onHoverMove: (viewerMarker, event) => {
              const marker = sourcePhotoMarkersById.get(String(viewerMarker.id));
              if (marker && isHeatMapEnabled) {
                showHeatMapTooltip(markerHeatMapSummaries.get(marker.id), event);
              }
            },
            onHoverEnd: () => {
              if (isHeatMapEnabled) hideHeatMapTooltip();
            },
            onTouchStartCapture: isPdfLayout
              ? undefined
              : (viewerMarker, event) => {
                  const marker = sourcePhotoMarkersById.get(String(viewerMarker.id));
                  if (marker) handlePhotoMarkerTouchStart(marker, event);
                },
            onTouchEndCapture: isPdfLayout
              ? undefined
              : (viewerMarker, event) => {
                  const marker = sourcePhotoMarkersById.get(String(viewerMarker.id));
                  if (marker) handlePhotoMarkerTouchEnd(marker, event);
                },
            onTouchCancelCapture: isPdfLayout
              ? undefined
              : () => {
                  markerTouchStartRef.current = null;
                },
            onDragStart: (viewerMarker, event) => {
              const marker = sourcePhotoMarkersById.get(String(viewerMarker.id));
              if (marker) handleMarkerDragStart?.(marker.id, event);
            },
            onContextMenu: (_viewerMarker, event) => {
              handleMarkerRightClick?.(event);
            },
            onDelete: (viewerMarker, event) => {
              event.stopPropagation();
              const marker = sourcePhotoMarkersById.get(String(viewerMarker.id));
              if (marker) deletePhotoMarker(marker.id);
            },
          },
          afterMarkers:
            isHeatMapEnabled && !photoIndexMode ? (
              <>
                {units
                  .filter((unit) => unit && Array.isArray(unit.points) && unit.points.length >= 3)
                  .map((unit) => {
                    if (selectedUnit?.id === unit.id) return null;
                    const viewerAreaTag = toViewerAreaTag(unit);
                    const summary = areaHeatMapSummaries.get(unit.id);
                    const validPoints = normalizeLayoutPolygon(
                      pendingPolygonChanges.get(unit.id) ?? viewerAreaTag.points
                    );
                    if (validPoints.length < 3) return null;
                    return (
                      <polygon
                        key={`heat-overlay-${unit.id}`}
                        points={validPoints.map((point) => point.join(',')).join(' ')}
                        opacity={
                          summary
                            ? issueCountMatchesRange(summary.counts.total, heatMapIssueFilter)
                              ? 1
                              : 0.18
                            : heatMapIssueFilter === 'all'
                              ? 1
                              : 0.18
                        }
                        style={{
                          fill: getHeatMapSoftColor(summary?.counts.total ?? 0),
                          fillOpacity: 0.5,
                          stroke: getHeatMapColor(summary?.counts.total ?? 0),
                          strokeOpacity: 0.58,
                          strokeWidth: 1.8,
                          strokeLinejoin: 'round',
                          strokeLinecap: 'round',
                          pointerEvents: 'none',
                        }}
                      />
                    );
                  })}
              </>
            ) : undefined,
          selectedAreaFocus:
            selectedUnit && mode !== 'areaTags'
              ? {
                  points: selectedUnit.points.map(([x, y]) => [x, y] as const),
                  width: imageDimensions.width,
                  height: imageDimensions.height,
                  scale,
                }
              : undefined,
          polygonEdit: {
            points: editingPolygonPoints,
            draggingNodeIndex,
            snapPosition: showSnapIndicator ? snapPosition : null,
            onNodeMouseDown: (index, event) => handleNodeDragStart(event, index),
          },
          areaTagDelete: {
            areaTags: viewerAreaTags,
            visible: showAreaDeleteControls,
            editingAreaTagId: editingPolygonId,
            getPoints: (areaTag) => pendingPolygonChanges.get(Number(areaTag.id)) ?? areaTag.points,
            getLabelPosition: (areaTag) =>
              pendingTextChanges.get(Number(areaTag.id))?.text_position ?? areaTag.label?.position,
            onDelete: (areaTag, event) => {
              const areaId = Number(areaTag.id);
              onDeleteAreaTag?.(areaId);
              if (editingPolygonId === areaId) {
                handlePolygonEditClick(areaId, event);
              }
            },
          },
        }}
      />

      {selectedUnit && mode !== 'areaTags' && (
        <SelectedAreaFocusControl
          unit={selectedUnit}
          containerRef={containerRef}
          svgRef={svgRef}
          position={position}
          scale={scale}
          isPdfLayout={isPdfLayout}
          onBackToOverview={onBackToOverview}
        />
      )}

      {canShowTooltips && isHeatMapEnabled && heatMapTooltip && (
        <div
          role="tooltip"
          className="fixed z-50 w-72 rounded-xl border border-border/80 bg-heatmap-panel px-3.5 py-3 text-card-foreground shadow-xl ring-1 ring-foreground/5 backdrop-blur-md pointer-events-none"
          style={{ left: heatMapTooltip.x + 12, top: heatMapTooltip.y + 12 }}
        >
          <div className="mb-2 flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold leading-tight">{heatMapTooltip.summary.label}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {heatMapTooltip.summary.photoCount} photo
                {heatMapTooltip.summary.photoCount === 1 ? '' : 's'} ·{' '}
                {heatMapTooltip.summary.hotspotCount} hotspot
                {heatMapTooltip.summary.hotspotCount === 1 ? '' : 's'}
              </p>
            </div>
            <span
              className="rounded-full px-2.5 py-1 text-xs font-semibold"
              style={{
                backgroundColor: getHeatMapSoftColor(heatMapTooltip.summary.counts.total),
                color: getHeatMapColor(heatMapTooltip.summary.counts.total),
              }}
            >
              {heatMapTooltip.summary.counts.total} open
            </span>
          </div>
          <div className="space-y-1.5 border-t border-border/60 pt-2 text-xs">
            {[
              ['Observations', heatMapTooltip.summary.counts.observations],
              ['Punch items', heatMapTooltip.summary.counts.punch_items],
              ['RFIs', heatMapTooltip.summary.counts.rfis],
              ['Submittals', heatMapTooltip.summary.counts.submittals],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-semibold text-foreground">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Editing Save/Cancel Panel */}
      {polygonEditingMode && pendingPolygonChanges.size > 0 && (
        <Card className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="text-sm text-muted-foreground">
                <span className="font-medium">{pendingPolygonChanges.size}</span> unsaved polygon
                change{pendingPolygonChanges.size !== 1 ? 's' : ''}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={onCancelTextChanges}>
                  Cancel
                </Button>
                <Button variant="default" size="sm" onClick={onSaveTextChanges}>
                  Save Changes
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Coordinates Panel */}
      {!isPdfLayout && showCoordinates && (
        <Card className="absolute bottom-20 right-4 w-80 max-h-96 overflow-y-auto">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex justify-between items-center">
              Polygon Coordinates
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(currentPolygon));
                  alert('Coordinates copied to clipboard!');
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-3 rounded-md whitespace-pre-wrap">
              {JSON.stringify(currentPolygon, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LayoutCanvas;
